// src/services/expenseServices.js
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL;

// Base URLs
const expenseBaseUrl = `${API_BASE}/api/expense`;
const usersUrl = `${API_BASE}/api/users`;

// Lit le token à chaque appel
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const computeExpenseDrivingDistance = async ({
  originAddress,
  destinationAddress,
}) => {
  const { data } = await axios.post(
    `${expenseBaseUrl}/claims/compute-distance`,
    { originAddress, destinationAddress },
    { headers: { ...authHeaders() } }
  );
  return data;
};

/* =========================
   PROFIL FRAIS (coordonnées)
   ========================= */

// Récupérer le profil de frais d’un user
export const getExpenseProfileByUser = async (userId) => {
  try {
    const { data } = await axios.get(`${usersUrl}/${userId}/expense-profile`, {
      headers: authHeaders(),
    });
    return data; // UserExpenseProfile | null
  } catch (error) {
    console.error("Erreur lors du chargement du profil de frais:", error);
    throw error;
  }
};

// Créer / Mettre à jour (upsert) le profil de frais d’un user
export const upsertExpenseProfileByUser = async (userId, payload) => {
  try {
    const { data } = await axios.post(
      `${usersUrl}/${userId}/expense-profile`,
      payload,
      {
        headers: authHeaders(),
      },
    );
    return data; // UserExpenseProfile
  } catch (error) {
    console.error("Erreur lors de la sauvegarde du profil de frais:", error);
    throw error;
  }
};

// Supprimer le profil de frais d’un user
export const deleteExpenseProfileByUser = async (userId) => {
  try {
    const { data } = await axios.delete(
      `${usersUrl}/${userId}/expense-profile`,
      {
        headers: authHeaders(),
      },
    );
    return data; // { ok: true }
  } catch (error) {
    console.error("Erreur lors de la suppression du profil de frais:", error);
    throw error;
  }
};

/* =========================
   CODES BUDGÉTAIRES
   ========================= */

// Liste des codes budgétaires
// options: { activeOnly?: boolean }
export const getBudgetCodes = async (options = {}) => {
  try {
    const params = {};
    if (options.activeOnly) params.activeOnly = 1;

    const { data } = await axios.get(`${expenseBaseUrl}/budget-codes`, {
      headers: authHeaders(),
      params,
    });
    return data; // Array<EventBudgetCode>
  } catch (error) {
    console.error("Erreur lors du chargement des codes budgétaires:", error);
    throw error;
  }
};

// Détail d’un code budgétaire
export const getBudgetCodeById = async (id) => {
  try {
    const { data } = await axios.get(`${expenseBaseUrl}/budget-codes/${id}`, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.error("Erreur lors du chargement du code budgétaire:", error);
    throw error;
  }
};

// CRUD codes budgétaires (admin)
export const createBudgetCode = async (payload) => {
  try {
    const { data } = await axios.post(
      `${expenseBaseUrl}/budget-codes`,
      payload,
      {
        headers: authHeaders(),
      },
    );
    return data;
  } catch (error) {
    console.error("Erreur lors de la création du code budgétaire:", error);
    throw error;
  }
};

export const updateBudgetCode = async (id, payload) => {
  try {
    const { data } = await axios.put(
      `${expenseBaseUrl}/budget-codes/${id}`,
      payload,
      {
        headers: authHeaders(),
      },
    );
    return data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du code budgétaire:", error);
    throw error;
  }
};

export const deleteBudgetCode = async (id) => {
  try {
    const { data } = await axios.delete(
      `${expenseBaseUrl}/budget-codes/${id}`,
      {
        headers: authHeaders(),
      },
    );
    return data; // { ok: true }
  } catch (error) {
    console.error("Erreur lors de la suppression du code budgétaire:", error);
    throw error;
  }
};

/* =========================
   FICHES DE FRAIS (CLAIMS)
   ========================= */

// Liste admin avec filtres (query params)
// filters: { status?, eventId?, userId?, budgetCodeId?, from?, to?, q? }
export const getExpenseClaims = async (filters = {}) => {
  try {
    const { data } = await axios.get(`${expenseBaseUrl}/claims`, {
      headers: authHeaders(),
      params: filters,
    });
    return data; // Array<ExpenseClaim>
  } catch (error) {
    console.error("Erreur lors du chargement des fiches de frais:", error);
    throw error;
  }
};

// Compteur
export const countExpenseClaims = async (params = {}) => {
  try {
    const { data } = await axios.get(`${expenseBaseUrl}/claims/count`, {
      headers: authHeaders(),
      params,
    });
    return data; // { total }
  } catch (error) {
    console.error("Erreur lors du comptage des fiches de frais:", error);
    throw error;
  }
};

// Stats (graphiques)
export const getExpenseClaimsStats = async () => {
  try {
    const { data } = await axios.get(`${expenseBaseUrl}/claims/stats`, {
      headers: authHeaders(),
    });
    return data; // { byStatus, byMonth }
  } catch (error) {
    console.error(
      "Erreur lors du chargement des stats fiches de frais:",
      error,
    );
    throw error;
  }
};

// Export (CSV Excel-friendly) — retourne un Blob
export const exportExpenseClaims = async (filters = {}) => {
  try {
    const response = await axios.get(`${expenseBaseUrl}/claims/export`, {
      headers: authHeaders(),
      params: filters,
      responseType: "blob",
    });
    return response.data; // Blob
  } catch (error) {
    console.error("Erreur lors de l'export des fiches de frais:", error);
    throw error;
  }
};

// Mes fiches (côté user)
export const getExpenseClaimsByUser = async (userId) => {
  try {
    const { data } = await axios.get(`${usersUrl}/${userId}/expense/claims`, {
      headers: authHeaders(),
    });
    return data; // Array<ExpenseClaim>
  } catch (error) {
    console.error(
      "Erreur lors du chargement des fiches de frais de l'utilisateur:",
      error,
    );
    throw error;
  }
};

// Détail d’une fiche
export const getExpenseClaimById = async (id) => {
  try {
    const { data } = await axios.get(`${expenseBaseUrl}/claims/${id}`, {
      headers: authHeaders(),
    });
    return data; // ExpenseClaim (+ items + attachments si inclus côté back)
  } catch (error) {
    console.error("Erreur lors du chargement de la fiche de frais:", error);
    throw error;
  }
};

// Créer une fiche
export const createExpenseClaim = async (payload) => {
  try {
    const { data } = await axios.post(`${expenseBaseUrl}/claims`, payload, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.error("CREATE CLAIM 400 =>", error?.response?.data || error);
    throw error;
  }
};

// Mettre à jour une fiche
export const updateExpenseClaim = async (id, payload) => {
  try {
    const { data } = await axios.put(
      `${expenseBaseUrl}/claims/${id}`,
      payload,
      {
        headers: authHeaders(),
      },
    );
    return data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour de la fiche de frais:", error);
    throw error;
  }
};

// Supprimer une fiche
export const deleteExpenseClaim = async (id) => {
  try {
    const { data } = await axios.delete(`${expenseBaseUrl}/claims/${id}`, {
      headers: authHeaders(),
    });
    return data; // { ok: true }
  } catch (error) {
    console.error("Erreur lors de la suppression de la fiche de frais:", error);
    throw error;
  }
};

/* =========================
   WORKFLOW (submit / admin)
   ========================= */

// Soumettre (draft -> submitted)
export const submitExpenseClaim = async (id) => {
  try {
    const { data } = await axios.post(
      `${expenseBaseUrl}/claims/${id}/submit`,
      null,
      {
        headers: authHeaders(),
      },
    );
    return data; // { ok, id, statut }
  } catch (error) {
    console.error("Erreur lors de la soumission de la fiche:", error);
    throw error;
  }
};

// Approuver (admin)
export const approveExpenseClaim = async (id, payload) => {
  // payload: { validated_by_user_id, admin_commentaire? }
  try {
    const { data } = await axios.post(
      `${expenseBaseUrl}/claims/${id}/approve`,
      payload,
      {
        headers: authHeaders(),
      },
    );
    return data;
  } catch (error) {
    console.error("Erreur lors de l'approbation de la fiche:", error);
    throw error;
  }
};

// Rejeter (admin)
export const rejectExpenseClaim = async (id, payload) => {
  // payload: { validated_by_user_id, admin_commentaire }
  try {
    const { data } = await axios.post(
      `${expenseBaseUrl}/claims/${id}/reject`,
      payload,
      {
        headers: authHeaders(),
      },
    );
    return data;
  } catch (error) {
    console.error("Erreur lors du rejet de la fiche:", error);
    throw error;
  }
};

// Marquer payé (admin)
export const markExpenseClaimPaid = async (id, payload = {}) => {
  // payload: { validated_by_user_id? }
  try {
    const { data } = await axios.post(
      `${expenseBaseUrl}/claims/${id}/mark-paid`,
      payload,
      {
        headers: authHeaders(),
      },
    );
    return data;
  } catch (error) {
    console.error("Erreur lors du passage en payé:", error);
    throw error;
  }
};

/* =========================
   LIGNES DE FRAIS (ITEMS)
   ========================= */

// Lignes d’une fiche
export const getExpenseItemsByClaim = async (claimId) => {
  try {
    const { data } = await axios.get(
      `${expenseBaseUrl}/claims/${claimId}/items`,
      {
        headers: authHeaders(),
      },
    );
    return data; // Array<ExpenseClaimItem>
  } catch (error) {
    console.error("Erreur lors du chargement des lignes:", error);
    throw error;
  }
};

// Ajouter une ligne
export const createExpenseItem = async (claimId, payload) => {
  try {
    const { data } = await axios.post(
      `${expenseBaseUrl}/claims/${claimId}/items`,
      payload,
      {
        headers: authHeaders(),
      },
    );
    return data;
  } catch (error) {
    console.error("Erreur lors de l'ajout d'une ligne:", error);
    throw error;
  }
};

// Modifier une ligne
export const updateExpenseItem = async (itemId, payload) => {
  try {
    const { data } = await axios.put(
      `${expenseBaseUrl}/items/${itemId}`,
      payload,
      {
        headers: authHeaders(),
      },
    );
    return data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour de la ligne:", error);
    throw error;
  }
};

// Supprimer une ligne
export const deleteExpenseItem = async (itemId) => {
  try {
    const { data } = await axios.delete(`${expenseBaseUrl}/items/${itemId}`, {
      headers: authHeaders(),
    });
    return data; // { ok: true }
  } catch (error) {
    console.error("Erreur lors de la suppression de la ligne:", error);
    throw error;
  }
};

/* =========================
   JUSTIFICATIFS (ATTACHMENTS)
   ========================= */

// Fichiers d’une fiche
export const getExpenseAttachmentsByClaim = async (claimId) => {
  try {
    const { data } = await axios.get(
      `${expenseBaseUrl}/claims/${claimId}/attachments`,
      {
        headers: authHeaders(),
      },
    );
    return data; // Array<ExpenseClaimAttachment>
  } catch (error) {
    console.error("Erreur lors du chargement des justificatifs:", error);
    throw error;
  }
};

// Upload justificatif sur la fiche (multipart ou file_url)
export const uploadExpenseAttachmentToClaim = async (
  claimId,
  payload,
  config = {},
) => {
  try {
    const headers = {
      ...authHeaders(),
      ...(config.headers || {}),
    };

    const { data } = await axios.post(
      `${expenseBaseUrl}/claims/${claimId}/attachments`,
      payload,
      { headers },
    );

    return data;
  } catch (error) {
    console.error("Erreur lors de l'upload du justificatif (fiche):", error);
    throw error;
  }
};

// Upload justificatif lié à une ligne
export const uploadExpenseAttachmentToItem = async (
  itemId,
  payload,
  config = {},
) => {
  try {
    // ✅ Laisse Axios gérer automatiquement le Content-Type + boundary si payload est un FormData
    const headers = {
      ...authHeaders(),
      ...(config.headers || {}),
    };

    const { data } = await axios.post(
      `${expenseBaseUrl}/items/${itemId}/attachments`,
      payload,
      { headers },
    );

    return data;
  } catch (error) {
    console.error("Erreur lors de l'upload du justificatif (ligne):", error);
    throw error;
  }
};

// Supprimer un justificatif
export const deleteExpenseAttachment = async (attachmentId) => {
  try {
    const { data } = await axios.delete(
      `${expenseBaseUrl}/attachments/${attachmentId}`,
      {
        headers: authHeaders(),
      },
    );
    return data; // { ok: true }
  } catch (error) {
    console.error("Erreur lors de la suppression du justificatif:", error);
    throw error;
  }
};
